import React from 'react';
import { useLocation } from 'react-router-dom';
import { Task, getPriorityLabel } from '@/types/tasks';
import { cn } from '@/lib/utils';
import { Clock, Calendar, Target } from 'lucide-react';
import GlassPill from '@/components/ui/GlassPill';
import MoveCardButton from '@/components/ui/MoveCardButton';
import { useIsMobile } from '@/hooks/layout/use-mobile';
import { useGoalsQuery } from '@/hooks/goal-planner/queries/use-goals-query';
import { Checkbox } from '@/components/ui/checkbox';

interface TaskCardGhostProps {
  task: Task;
}

/**
 * Enhanced ghost component for task drag overlay
 * Matches the actual TaskCard design with drag button and all badges
 */
const TaskCardGhost: React.FC<TaskCardGhostProps> = ({ task }) => {
  const isCompleted = task.isCompleted;
  const { isMobile } = useIsMobile();
  const location = useLocation();
  
  // Auto-detect if we're on a project page to hide goal name
  const isOnProjectPage = location.pathname.includes('/project/');
  
  // Get goals data to find the goal name
  const { data: goals = [] } = useGoalsQuery();
  const goal = task.goalId ? goals.find(g => g.id === task.goalId) : null;
  
  // Format the created date if it exists, otherwise use a fallback
  const createdDate = task.createdAt 
    ? new Date(task.createdAt) 
    : new Date(Date.now() - Math.floor(Math.random() * 30) * 24 * 60 * 60 * 1000);

  // Map task priority to GlassPill variant
  const getPriorityVariant = () => {
    switch (task.priority) {
      case 'high': 
        return 'red';
      case 'medium': 
        return 'amber';
      case 'not-important': 
        return 'green';
      default: 
        return 'blue';
    }
  };

  // Check if task is overdue - this hook must be called before any early returns
  const isOverdue = React.useMemo(() => {
    if (isCompleted || !task.dueDate) return false;
    const dueDate = new Date(task.dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    return dueDate < today;
  }, [task.dueDate, isCompleted]);

  return (
    <div className="group relative">
      <div 
        className="relative rounded-lg overflow-hidden h-full flex flex-col cursor-pointer shadow-sm border-2 border-brandBlue/30 dark:border-brandPurple/30 bg-gradient-to-br from-brandBlue/10 to-brandPurple/10"
        style={{ 
          borderRadius: '0.5rem',
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
          willChange: 'transform, box-shadow',
          transformStyle: 'preserve-3d',
          WebkitFontSmoothing: 'antialiased'
        }}
      >
        {/* Beautiful glass morphism background */}
        <div 
          className="absolute inset-0 bg-gradient-to-br from-white/40 via-white/20 to-white/5 dark:from-black/60 dark:to-black/30 backdrop-blur-xl backdrop-saturate-150 -z-10 will-change-transform" 
          style={{ 
            transform: 'translateZ(0)',
            borderRadius: 'inherit'
          }}
        />

      {/* Checkbox Overlay - positioned to align with text center */}
      <div 
        className="absolute z-20 flex-shrink-0"
        style={{
          top: isMobile ? '16px' : '24px',
          left: isMobile ? '12px' : '20px',
        }}
      >
        <Checkbox
          checked={isCompleted}
          className="transition-all duration-200 bg-white/90 dark:bg-gray-800/90 pointer-events-none"
        />
      </div>

      {/* Move button in top right corner */}
      <div className="absolute top-3 right-3 z-10">
        <MoveCardButton
          variant="glass"
          size="sm"
          iconType="vertical"
          className="opacity-70 pointer-events-none"
        />
      </div>

      <div 
        className={`${isMobile ? 'p-3 pb-4' : 'p-5 pb-6'} flex flex-col h-full`} 
        style={{ 
          willChange: 'contents',
          borderRadius: 'inherit'
        }}
      >
        <div className={cn(
          "flex items-start gap-3 w-full h-full min-w-0",
          "pr-8", // Right padding to avoid overlap with move button
          { "opacity-70": isCompleted }
        )}>
          <div className="flex-1 min-w-0 overflow-hidden">
            {/* Top row with title */}
            <div className="flex items-center mb-2">
              <div className="flex items-center min-w-0 w-full">
                {/* Checkbox spacing - matches the positioned checkbox size */}
                <div className="flex-shrink-0 w-6 h-6 mr-2"></div>
                
                {/* Task title - strict overflow handling */}
                <div className="flex-1 min-w-0" style={{ overflow: 'hidden' }}>
                  <h3 
                    className={cn(
                      "text-sm font-medium transition-all",
                      isCompleted && "text-muted-foreground line-through"
                    )}
                    style={{ 
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={task.title}
                  >
                    {task.title}
                  </h3>
                </div>
              </div>
            </div>
            
            {/* Description */}
            {task.description && (
              <div className="mb-3 pb-1" style={{ overflow: 'hidden' }}>
                <p 
                  className={cn(
                    "text-sm transition-all duration-200",
                    isCompleted ? "text-muted-foreground/40" : "text-muted-foreground"
                  )}
                  style={{
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    wordBreak: 'break-word'
                  }}
                  title={task.description}
                >
                  {task.description}
                </p>
              </div>
            )}

            {/* Footer with dates and badges - matching TaskCard layout */}
            <div className={cn(
              "flex items-center mt-1 gap-2 min-w-0",
              isMobile ? "justify-end" : "justify-between"
            )}>
              {/* Due date and goal on the left - hidden on mobile */}
              {!isMobile && (
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  {/* Due Date */}
                  {task.dueDate && (
                    <div className="flex items-center gap-1">
                      <Calendar size={12} />
                      <span>Due: {new Date(task.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                    </div>
                  )}
                  
                  {/* Goal Name - only show if not on project page and goal exists */}
                  {!isOnProjectPage && goal && (
                    <div className="flex items-center gap-1">
                      <Target size={12} className="text-purple-600 dark:text-purple-400" />
                      <span>Project: {goal.projectTitle || goal.title}</span>
                    </div>
                  )}
                </div>
              )}
              
              {/* Badges on the right - hidden on mobile */}
              {!isMobile && (
                <div className="flex items-center gap-2">
                  {/* Overdue badge */}
                  {isOverdue && (
                    <GlassPill
                      type="Overdue"
                      variant="red"
                      icon={<Clock size={12} className="text-red-500" />}
                      iconPosition="left"
                      className={cn(
                        "text-xs py-0.5 px-2"
                      )}
                    />
                  )}

                  {/* Priority badge */}
                  {task.priority && (
                    <GlassPill
                      type={getPriorityLabel(task.priority)}
                      variant={getPriorityVariant()}
                      className={cn(
                        "text-xs py-0.5 px-2", 
                        isCompleted ? "opacity-50" : "opacity-100"
                      )}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default TaskCardGhost; 